import { useState } from 'react'
import {
    AccountRecord,
    ClassifierDetailView,
    useCreateModerationBoundaryMutation
} from '../../api/client'
import { Queue } from '../../models/Moderation'
import Button from '../Button'
import ArwenListBox, { ArwenListBoxItem } from '../inputs/ArwenListBox'
import SimpleLoader from '../SimpleLoader'
import { toastError, toastSuccess } from '../Notification'
import { PROMPT_MANAGED_MESSAGE } from './promptManaged'

type Props = {
    account: AccountRecord
    classifierDetailsList: ClassifierDetailView[]
    queue: Queue
    /** Classifier ids whose boundaries are owned by classifier deployment. */
    promptManagedClassifierIds: Set<number>
    onCreate: () => void
}

export default function NewModerationBoundary(props: Props) {
    const { account, queue, classifierDetailsList, promptManagedClassifierIds, onCreate } = props
    const [selectedItem, setSelectedItem] = useState<ArwenListBoxItem>()
    const [probability, setProbability] = useState<number>(0.5)

    const [createModerationBoundary, { loading: createModerationBoundaryLoading }] =
        useCreateModerationBoundaryMutation({
            onError: () => {
                toastError('Failed to create moderation boundary')
            },
            onCompleted: () => {
                toastSuccess('Moderation boundary created')
            }
        })

    // Prompt-managed classifiers stay in the list but can't be picked: which
    // profiles they apply to is decided by deploying the classifier, not by adding
    // a boundary here.
    const listBoxItems: ArwenListBoxItem[] = classifierDetailsList.map((classifier) => {
        return {
            id: `${classifier.id}` || '',
            name: `${classifier.displayName} (${classifier.shortName})`,
            disabled: promptManagedClassifierIds.has(classifier.id),
            disabledReason: PROMPT_MANAGED_MESSAGE
        }
    })

    const selectedClassifier = classifierDetailsList.find(
        (item) => `${item.id}` === selectedItem?.id
    )

    return (
        <div className="flex flex-col gap-2">
            <div className="text-sm font-medium text-ink-800">Add a classifier</div>
            <div className="flex flex-wrap items-center gap-3 text-gray-950">
                <div>
                    <ArwenListBox
                        value={
                            selectedItem || {
                                id: '',
                                name: 'Select a classifier...',
                                icon: undefined
                            }
                        }
                        options={listBoxItems}
                        onSelect={setSelectedItem}
                        widthClass="w-56"
                    />
                </div>

                {selectedClassifier && (
                    <input
                        type="number"
                        name="probability"
                        id="probability"
                        className="block h-9 w-20 rounded-md border border-ink-200 text-sm shadow-sm focus:border-arwen-500 focus:outline-none focus:ring-1 focus:ring-arwen-500"
                        value={probability}
                        step="0.05"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setProbability(Number.parseFloat(event.target.value))
                        }}
                    />
                )}
                {!createModerationBoundaryLoading && (
                    <Button
                        primary={true}
                        text={'Create'}
                        onClick={async () => {
                            if (selectedClassifier?.id) {
                                await createModerationBoundary({
                                    variables: {
                                        moderationBoundary: {
                                            accountId: account.id,
                                            classifierId: selectedClassifier.id,
                                            queue: queue,
                                            probability
                                        }
                                    }
                                })
                                onCreate()
                            }
                        }}
                    />
                )}
                <SimpleLoader loading={createModerationBoundaryLoading} />
            </div>
        </div>
    )
}
